import { OVERVIEW_TABS } from 'cpool-constants';
import { z } from 'zod';

import { LEAD_DETAILS_OVERLAYS } from '../constants/overlays';
import { CREATE_APPLICANT_TAB_PATHS, OVERVIEW_TAB_PATHS } from '../constants/tabPaths';

const LEAD_DETAILS_ROUTE_SCOPES = {
    BANK_REQUEST: OVERVIEW_TAB_PATHS[OVERVIEW_TABS.BANK_REQUEST],
    CUSTOMER_DATA: OVERVIEW_TAB_PATHS[OVERVIEW_TABS.CUSTOMER_DATA],
    OFFER_MANAGEMENT: OVERVIEW_TAB_PATHS[OVERVIEW_TABS.OFFER_MANAGEMENT],
} as const;

const trueSearchParam = z.literal(true).optional().catch(undefined);
const uuidSearchParam = z.string().uuid().optional().catch(undefined);

export const LEAD_DETAILS_OVERLAY_CONFIG = {
    [LEAD_DETAILS_OVERLAYS.BUCKET_HISTORY]: { routeScope: null, schema: trueSearchParam },
    [LEAD_DETAILS_OVERLAYS.CONSORS_NOTES]: {
        routeScope: LEAD_DETAILS_ROUTE_SCOPES.OFFER_MANAGEMENT,
        schema: trueSearchParam,
    },
    [LEAD_DETAILS_OVERLAYS.CREATE_APPLICANT]: {
        routeScope: LEAD_DETAILS_ROUTE_SCOPES.CUSTOMER_DATA,
        schema: trueSearchParam,
    },
    [LEAD_DETAILS_OVERLAYS.CREATE_OFFER]: {
        routeScope: LEAD_DETAILS_ROUTE_SCOPES.OFFER_MANAGEMENT,
        schema: trueSearchParam,
    },
    [LEAD_DETAILS_OVERLAYS.CREDIT_WORTHINESS_ID]: {
        routeScope: LEAD_DETAILS_ROUTE_SCOPES.OFFER_MANAGEMENT,
        schema: uuidSearchParam,
    },
    [LEAD_DETAILS_OVERLAYS.DUPLICATES]: { routeScope: null, schema: trueSearchParam },
    [LEAD_DETAILS_OVERLAYS.EDIT_OFFER_ID]: {
        routeScope: LEAD_DETAILS_ROUTE_SCOPES.OFFER_MANAGEMENT,
        schema: uuidSearchParam,
    },
    [LEAD_DETAILS_OVERLAYS.EUROPACE_EVENTS]: { routeScope: null, schema: trueSearchParam },
    [LEAD_DETAILS_OVERLAYS.FORM_LOANS]: { routeScope: LEAD_DETAILS_ROUTE_SCOPES.BANK_REQUEST, schema: trueSearchParam },
    [LEAD_DETAILS_OVERLAYS.LEGAL_CONSENT]: { routeScope: null, schema: trueSearchParam },
    [LEAD_DETAILS_OVERLAYS.OFFER_EXTRA_INFO_ID]: {
        routeScope: LEAD_DETAILS_ROUTE_SCOPES.BANK_REQUEST,
        schema: uuidSearchParam,
    },
    [LEAD_DETAILS_OVERLAYS.OFFER_INFO_ID]: {
        routeScope: LEAD_DETAILS_ROUTE_SCOPES.OFFER_MANAGEMENT,
        schema: uuidSearchParam,
    },
    [LEAD_DETAILS_OVERLAYS.PARTNER]: { routeScope: null, schema: trueSearchParam },
    [LEAD_DETAILS_OVERLAYS.SCHUFA]: { routeScope: null, schema: trueSearchParam },
    [LEAD_DETAILS_OVERLAYS.TRACKING_UTM]: { routeScope: null, schema: trueSearchParam },
} as const;

export type LeadDetailsOverlayKey = keyof typeof LEAD_DETAILS_OVERLAY_CONFIG;

export const LEAD_DETAILS_OVERLAY_KEYS = Object.keys(LEAD_DETAILS_OVERLAY_CONFIG) as Array<LeadDetailsOverlayKey>;

const overlaySchemaShape = Object.fromEntries(
    LEAD_DETAILS_OVERLAY_KEYS.map((key) => [key, LEAD_DETAILS_OVERLAY_CONFIG[key].schema]),
) as {
    [Key in LeadDetailsOverlayKey]: (typeof LEAD_DETAILS_OVERLAY_CONFIG)[Key]['schema'];
};

export const leadDetailsSearchSchema = z.object({
    ...overlaySchemaShape,
    createApplicantTab: z
        .enum([
            CREATE_APPLICANT_TAB_PATHS.EMPLOYMENT,
            CREATE_APPLICANT_TAB_PATHS.EXISTING_CREDITS,
            CREATE_APPLICANT_TAB_PATHS.INCOME_AND_EXPENSES,
            CREATE_APPLICANT_TAB_PATHS.LIVING_SITUATION,
            CREATE_APPLICANT_TAB_PATHS.PERSONAL_DATA,
            CREATE_APPLICANT_TAB_PATHS.REAL_ESTATE,
        ])
        .optional()
        .catch(undefined),
});

export type LeadDetailsOverlaySearch = z.infer<typeof leadDetailsSearchSchema>;
export type LeadDetailsOverlayValue<Key extends LeadDetailsOverlayKey> = NonNullable<LeadDetailsOverlaySearch[Key]>;

export type LeadDetailsModal = {
    [Key in LeadDetailsOverlayKey]: NonNullable<LeadDetailsOverlaySearch[Key]> extends true ? Key : never;
}[LeadDetailsOverlayKey];
